import { createRouter, createWebHistory } from 'vue-router'

// Route components are lazy-loaded so the main chat bundle doesn't grow with
// RAG Studio (and vice versa).
const Home = () => import('./App.vue')
const RAGShell = () => import('./components/RAGShell.vue')

// RAG Studio pages (src/rag/pages). Route names are the shared contract —
// link with { name, params }, never hand-built paths.
const HomePage = () => import('./rag/pages/HomePage.vue')
const CreatePage = () => import('./rag/pages/CreatePage.vue')
const ImportWizardPage = () => import('./rag/pages/ImportWizardPage.vue')
const ReviewInboxPage = () => import('./rag/pages/ReviewInboxPage.vue')
const AccessDiagnosticPage = () => import('./rag/pages/AccessDiagnosticPage.vue')
const InstanceLayout = () => import('./rag/pages/InstanceLayout.vue')
const OverviewTab = () => import('./rag/pages/OverviewTab.vue')
const ContentTab = () => import('./rag/pages/ContentTab.vue')
const SettingsTab = () => import('./rag/pages/SettingsTab.vue')
const VersionsTab = () => import('./rag/pages/VersionsTab.vue')
const VersionEditor = () => import('./rag/pages/VersionEditor.vue')
const QualityLayout = () => import('./rag/pages/QualityLayout.vue')
const TestSetsPage = () => import('./rag/pages/TestSetsPage.vue')
const RunsPage = () => import('./rag/pages/RunsPage.vue')
const RunDetailPage = () => import('./rag/pages/RunDetailPage.vue')
const ConversationsPage = () => import('./rag/pages/ConversationsPage.vue')
const ComparePage = () => import('./rag/pages/ComparePage.vue')
const MetricsPage = () => import('./rag/pages/MetricsPage.vue')

// Child pages get `versionId` / `runId` from the URL as props (only when the
// route has them); InstanceLayout adds `instance` + `user` on its <router-view>.
// The instance id is deliberately not passed — use `instance.id`.
const paramProps = (route) => {
  const props = {}
  if (route.params.versionId) props.versionId = String(route.params.versionId)
  if (route.params.runId) props.runId = String(route.params.runId)
  return props
}

const routes = [
  {
    path: '/',
    name: 'home',
    component: Home,
  },

  // RAG Studio — RAGShell wraps every /rag/* route with the sidebar + topbar.
  {
    path: '/rag',
    component: RAGShell,
    children: [
      { path: '', name: 'rag-home', component: HomePage },
      { path: 'new', name: 'rag-new', component: CreatePage },
      { path: 'import', name: 'rag-import', component: ImportWizardPage },
      { path: 'review', name: 'rag-review', component: ReviewInboxPage },
      // Staff-only; the backend enforces is_staff.
      { path: 'access-diagnostic', name: 'rag-access-diagnostic', component: AccessDiagnosticPage },
      // One assistant's workspace: header + tabs, loads the instance and
      // passes `instance` + `user` to every child. SMEs are redirected from
      // builder-only tabs to Quality inside InstanceLayout (it knows my_role).
      {
        path: ':id',
        component: InstanceLayout,
        props: true,
        children: [
          { path: '', name: 'rag-overview', component: OverviewTab, props: paramProps },
          { path: 'content', name: 'rag-content', component: ContentTab, props: paramProps },
          { path: 'settings', name: 'rag-settings', component: SettingsTab, props: paramProps },
          { path: 'versions', name: 'rag-versions', component: VersionsTab, props: paramProps },
          { path: 'versions/:versionId', name: 'rag-version', component: VersionEditor, props: paramProps },
          {
            path: 'quality',
            component: QualityLayout,
            props: paramProps,
            children: [
              { path: '', name: 'rag-quality', component: TestSetsPage, props: paramProps },
              { path: 'runs', name: 'rag-runs', component: RunsPage, props: paramProps },
              { path: 'runs/:runId', name: 'rag-run', component: RunDetailPage, props: paramProps },
              { path: 'conversations', name: 'rag-conversations', component: ConversationsPage, props: paramProps },
              { path: 'compare', name: 'rag-compare', component: ComparePage, props: paramProps },
              { path: 'metrics', name: 'rag-metrics', component: MetricsPage, props: paramProps },
            ],
          },
          // Unknown tab (e.g. /rag/:id/overview) → the assistant's default
          // page; InstanceLayout's SME redirect to Quality still applies.
          // Catch-alls rank below every named route, so those always win.
          {
            path: ':rest(.*)*',
            redirect: (to) => ({ name: 'rag-overview', params: { id: to.params.id } }),
          },
        ],
      },
      { path: ':rest(.*)*', redirect: { name: 'rag-home' } },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

export default router
